import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'


function Home() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 })

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY })
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1a3e] via-[#2d1b4e] to-[#1a1a3e] flex flex-col items-center justify-center p-4 overflow-hidden relative touch-none">
      {/* Detailed twinkling stars background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(150)].map((_, i) => {
          const size = 1 + (i % 4)
          const duration = 2 + (i % 3)
          const isYellow = i % 7 === 0
          return (
            <div
              key={i}
              className={`absolute rounded-full ${isYellow ? 'bg-yellow-100' : 'bg-white'}`}
              style={{
                width: size + 'px',
                height: size + 'px',
                left: Math.random() * 100 + '%',
                top: Math.random() * 100 + '%',
                animation: `twinkle ${duration}s infinite`,
                animationDelay: Math.random() * 3 + 's',
                opacity: 0.3 + (i % 4) * 0.15,
                boxShadow: size > 2 ? `0 0 ${size * 2}px ${isYellow ? 'rgba(254,243,199,0.6)' : 'rgba(255,255,255,0.5)'}` : 'none'
              }}
            />
          )
        })}
      </div>

      {/* Crescent moon (no shadow) */}
      <div className="absolute top-10 right-10 md:top-16 md:right-16 pointer-events-none">
        <div className="relative w-16 h-16 md:w-20 md:h-20">
          {/* Moon circle with realistic gradient */}
          <div className="absolute inset-0 bg-gradient-radial from-[#fff9e6] via-[#ffd700] to-[#ffa500] rounded-full" style={{
            background: 'radial-gradient(circle at 30% 30%, #fff9e6 0%, #ffd700 60%, #ffa500 100%)'
          }} />
          {/* Dark overlay for crescent */}
          <div className="absolute top-[-2px] right-[-8px] w-16 h-16 md:w-20 md:h-20 bg-gradient-to-b from-[#1a1a3e] via-[#2d1b4e] to-[#1a1a3e] rounded-full" />
        </div>
      </div>

      {/* Realistic planets */}
      {/* Purple gas giant */}
      <div className="absolute top-[20%] left-[15%] pointer-events-none">
        <div className="relative w-10 h-10 md:w-12 md:h-12">
          <div className="absolute inset-0 rounded-full" style={{
            background: 'radial-gradient(circle at 35% 35%, #e9d5ff 0%, #c084fc 30%, #9333ea 70%, #581c87 100%)'
          }} />
          {/* Atmospheric band */}
          <div className="absolute top-1/2 left-0 w-full h-1.5 bg-purple-800 opacity-30 -translate-y-1/2" />
        </div>
      </div>

      {/* Earth-like blue planet */}
      <div className="absolute bottom-[25%] right-[10%] pointer-events-none">
        <div className="relative w-6 h-6">
          <div className="absolute inset-0 rounded-full" style={{
            background: 'radial-gradient(circle at 35% 35%, #dbeafe 0%, #60a5fa 30%, #1d4ed8 70%, #1e3a8a 100%)'
          }} />
          {/* Cloud pattern */}
          <div className="absolute top-1/2 left-1/4 w-3 h-1.5 bg-white rounded-full opacity-30 -translate-y-1/2" />
        </div>
      </div>

      {/* Teal ice planet */}
      <div className="absolute bottom-[30%] left-[8%] pointer-events-none">
        <div className="relative w-5 h-5">
          <div className="absolute inset-0 rounded-full" style={{
            background: 'radial-gradient(circle at 35% 35%, #ccfbf1 0%, #5eead4 30%, #0d9488 70%, #134e4a 100%)'
          }} />
          {/* Ice cap */}
          <div className="absolute top-0 left-1/2 w-2 h-2 bg-white rounded-full opacity-60 -translate-x-1/2" />
        </div>
      </div>

      {/* Content - Centered Simple Layout */}
      <div className="relative z-10 flex flex-col items-center justify-center space-y-6">
        {/* BETA IS LIVE Badge */}
        <div className="bg-gradient-to-r from-purple-500 via-purple-400 to-purple-500 text-white px-6 py-2 rounded-full font-bold text-sm md:text-base shadow-[0_0_30px_rgba(168,85,247,0.6)] border-2 border-purple-300 animate-pulse flex items-center gap-2">
          BETA IS LIVE
        </div>
        
        {/* Logo */}
        <img 
          src="https://static.lumi.new/5f/5ffb7827bd4e3986904b9e50ce1b0447.webp"
          alt="Rocket Bunny Logo"
          className="w-64 md:w-96 mb-2 drop-shadow-[0_0_20px_rgba(220,38,38,0.8)] animate-pulse"
        />

        {/* Character with glow effect */}
        <div className="my-6 md:my-8 relative">
          <div className="absolute inset-0 bg-blue-500 rounded-full blur-2xl opacity-50 animate-pulse" />
          <img 
            src="https://static.lumi.new/7b/7b3b9738bd14cb21ec880e638b7612fd.webp"
            alt="Rocket Bunny"
            className="w-24 h-24 md:w-32 md:h-32 mx-auto relative z-10 drop-shadow-[0_0_30px_rgba(96,165,250,0.8)] hover:scale-110 transition-transform duration-300"
          />
        </div>

        {/* Buttons with enhanced effects */}
        <div className="flex flex-col gap-4 items-center">
          <button
            onClick={() => navigate('/game')}
            className="w-36 md:w-44 px-4 md:px-6 py-2 md:py-3 bg-gradient-to-br from-red-500 via-red-600 to-red-700 text-white text-base md:text-lg font-bold rounded-lg hover:from-red-600 hover:via-red-700 hover:to-red-800 active:scale-95 transition-all duration-200 border-2 border-white shadow-[0_4px_0_0_rgba(139,0,0,1),0_0_20px_rgba(220,38,38,0.4)] hover:shadow-[0_4px_0_0_rgba(139,0,0,1),0_0_30px_rgba(220,38,38,0.6)] active:shadow-[0_2px_0_0_rgba(139,0,0,1)] active:translate-y-1 touch-manipulation relative overflow-hidden group text-center"
            style={{ textShadow: '1px 1px 0 rgba(0,0,0,0.4)' }}
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 group-hover:animate-[shimmer_1s_ease-in-out] translate-x-[-200%]" />
            PLAY NOW
          </button>
          
          <button
            onClick={() => navigate('/burn-dashboard')}
            className="w-36 md:w-44 px-4 md:px-6 py-2 md:py-3 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 text-white text-base md:text-lg font-bold rounded-lg hover:from-orange-600 hover:via-orange-700 hover:to-orange-800 active:scale-95 transition-all duration-200 border-2 border-white shadow-[0_4px_0_0_rgba(154,52,18,1),0_0_20px_rgba(249,115,22,0.4)] hover:shadow-[0_4px_0_0_rgba(154,52,18,1),0_0_30px_rgba(249,115,22,0.6)] active:shadow-[0_2px_0_0_rgba(154,52,18,1)] active:translate-y-1 touch-manipulation relative overflow-hidden group text-center"
            style={{ textShadow: '1px 1px 0 rgba(0,0,0,0.4)' }}
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 group-hover:animate-[shimmer_1s_ease-in-out] translate-x-[-200%]" />
            BURN METER
          </button>
          
          <button
            onClick={() => navigate('/leaderboard')}
            className="w-36 md:w-44 px-4 md:px-6 py-2 md:py-3 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 text-black text-base md:text-lg font-bold rounded-lg hover:from-yellow-500 hover:via-yellow-600 hover:to-yellow-700 active:scale-95 transition-all duration-200 border-2 border-white shadow-[0_4px_0_0_rgba(180,83,9,1),0_0_20px_rgba(251,191,36,0.4)] hover:shadow-[0_4px_0_0_rgba(180,83,9,1),0_0_30px_rgba(251,191,36,0.6)] active:shadow-[0_2px_0_0_rgba(180,83,9,1)] active:translate-y-1 touch-manipulation relative overflow-hidden group text-center"
            style={{ textShadow: '1px 1px 0 rgba(0,0,0,0.15)' }}
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 group-hover:animate-[shimmer_1s_ease-in-out] translate-x-[-200%]" />
            LEADERBOARD
          </button>
          
          <button
            onClick={() => navigate(user ? '/profile' : '/login')}
            className="w-36 md:w-44 px-4 md:px-6 py-2 md:py-3 bg-gradient-to-br from-white via-gray-100 to-gray-200 text-black text-base md:text-lg font-bold rounded-lg hover:from-gray-100 hover:via-gray-200 hover:to-gray-300 active:scale-95 transition-all duration-200 border-2 border-gray-400 shadow-[0_4px_0_0_rgba(75,85,99,1),0_0_20px_rgba(255,255,255,0.2)] hover:shadow-[0_4px_0_0_rgba(75,85,99,1),0_0_30px_rgba(255,255,255,0.4)] active:shadow-[0_2px_0_0_rgba(75,85,99,1)] active:translate-y-1 touch-manipulation relative overflow-hidden group text-center"
            style={{ textShadow: '1px 1px 0 rgba(255,255,255,0.3)' }}
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 group-hover:animate-[shimmer_1s_ease-in-out] translate-x-[-200%]" />
            {user ? 'VIEW PROFILE' : 'LOGIN'}
          </button>
        </div>

        {/* Enhanced Moon decoration - removed, now in top right */}

        {/* Floating asteroids decoration */}
        <div className="absolute bottom-10 left-10 pointer-events-none opacity-40 animate-bounce" style={{ animationDuration: '3s' }}>
          <div className="text-4xl md:text-5xl">☄️</div>
        </div>
      </div>

    </div>
  )
}

export default Home
